import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription, ProFeature } from '@/hooks/useSubscription';
import { Lock, Waves, Bell, Heart, BookOpen, Calendar } from 'lucide-react';

// ── Feature metadata ──────────────────────────────────────────────────────────

const FEATURE_META: Record<
  ProFeature,
  { label: string; description: string; icon: React.ReactNode }
> = {
  surf_alerts: {
    label: 'Surf Alerts',
    description: 'Get a push notification when your break fires — the night before it matters.',
    icon: <Bell className="w-6 h-6" />,
  },
  favourite_breaks: {
    label: 'Favourite Breaks',
    description: 'Pin your home breaks for one-tap forecasts every session.',
    icon: <Heart className="w-6 h-6" />,
  },
  session_logging: {
    label: 'Session Log',
    description: 'Track every surf — conditions, gear, notes. Your personal logbook.',
    icon: <BookOpen className="w-6 h-6" />,
  },
  extended_forecast: {
    label: '16-Day Forecast',
    description: 'Plan further ahead. See swell, wind, and tide up to 16 days out.',
    icon: <Calendar className="w-6 h-6" />,
  },
};

// ── ProGate ───────────────────────────────────────────────────────────────────

interface ProGateProps {
  feature: ProFeature;
  children: React.ReactNode;
  /** If true, renders children blurred with overlay rather than hiding them */
  blur?: boolean;
}

export function ProGate({ feature, children, blur = true }: ProGateProps) {
  const { isPro, isLoading } = useSubscription();
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();

  // While loading, render children normally to avoid layout shift
  if (isLoading) return <>{children}</>;

  // Pro user — render normally
  if (isPro) return <>{children}</>;

  // Free user
  const meta = FEATURE_META[feature];

  return (
    <>
      {/* Locked content */}
      <div
        className="relative select-none"
        onClick={() => setModalOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setModalOpen(true)}
        aria-label={`Unlock ${meta.label} with Pro`}
      >
        {blur ? (
          <>
            <div className="pointer-events-none blur-[3px] opacity-50">
              {children}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 cursor-pointer">
              <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl px-4 py-3 flex items-center gap-2 shadow-lg border border-white/10">
                <Lock className="w-4 h-4 text-sky-400" />
                <span className="text-sm font-medium text-white">Pro feature — tap to unlock</span>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-10 cursor-pointer text-center">
            <div className="w-12 h-12 rounded-full bg-sky-500/10 border border-sky-500/20 flex items-center justify-center text-sky-400">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-white">{meta.label}</p>
              <p className="text-sm text-slate-400 mt-0.5">Available on Pro</p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <ProUpgradeModal
          feature={feature}
          onClose={() => setModalOpen(false)}
          onUpgrade={() => {
            setModalOpen(false);
            navigate('/subscribe');
          }}
        />
      )}
    </>
  );
}

// ── ProUpgradeModal ───────────────────────────────────────────────────────────

interface ProUpgradeModalProps {
  feature: ProFeature;
  onClose: () => void;
  onUpgrade: () => void;
}

function ProUpgradeModal({ feature, onClose, onUpgrade }: ProUpgradeModalProps) {
  const meta = FEATURE_META[feature];

  // All pro features listed for social proof
  const allFeatures: { icon: React.ReactNode; label: string }[] = [
    { icon: <Bell className="w-4 h-4" />, label: 'Surf alerts — night-before push notifications' },
    { icon: <Heart className="w-4 h-4" />, label: 'Favourite breaks — one-tap access' },
    { icon: <BookOpen className="w-4 h-4" />, label: 'Session logging' },
    { icon: <Calendar className="w-4 h-4" />, label: '16-day extended forecast' },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 pb-[calc(env(safe-area-inset-bottom)+88px)] sm:pb-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Sheet */}
      <div
        className="relative z-10 w-full max-w-sm bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Wave accent bar */}
        <div className="h-1 w-full bg-gradient-to-r from-sky-500 via-cyan-400 to-teal-500" />

        <div className="p-6 space-y-5">
          {/* Feature highlight */}
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-sky-500/15 border border-sky-500/20 flex items-center justify-center text-sky-400 shrink-0">
              {meta.icon}
            </div>
            <div>
              <h2 className="text-white font-semibold text-base leading-tight">{meta.label}</h2>
              <p className="text-slate-400 text-sm mt-1 leading-snug">{meta.description}</p>
            </div>
          </div>

          <hr className="border-white/8" />

          {/* All Pro features */}
          <div className="space-y-2.5">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Everything in Pro</p>
            {allFeatures.map(({ icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm text-slate-300">
                <span className="text-sky-400 shrink-0">{icon}</span>
                {label}
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="bg-white/5 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-white font-semibold text-lg">R79 <span className="text-slate-400 text-sm font-normal">/ month</span></p>
              <p className="text-sky-400 text-xs mt-0.5">or R599/year — save 37%</p>
            </div>
            <Waves className="w-8 h-8 text-sky-500/40" />
          </div>

          {/* CTAs */}
          <div className="space-y-2">
            <button
              onClick={onUpgrade}
              className="w-full py-3 rounded-xl bg-sky-500 hover:bg-sky-400 active:bg-sky-600 text-white font-semibold text-sm transition-colors"
            >
              Go Pro
            </button>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl text-slate-400 hover:text-slate-300 text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
